import { useEffect, useRef } from 'react';

import { Transaction, TransactionApi, TransactionSortField } from 'apis/TransactionApi';
import type { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortDescriptor } from 'utils/dataQuery';
import { GMetrixTable } from 'components';
import { useSuperState } from 'hooks';

type AccessCodeLookupModalProps = {
    onClose: () => void;
    onSelect: (accessCode: string) => void;
    resourceKey?: string;
    show: boolean;
};

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 1000;

export const AccessCodeLookupModal = (props: AccessCodeLookupModalProps) => {

    const { onClose, onSelect, resourceKey, show } = props;

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        accessCodes: [] as Transaction[],
        debouncedSearchTerm: '',
        loading: false,
        page: 1,
        pageSize: PAGE_SIZE,
        searchTerm: '',
        serverTotalCount: 0,
        sortDir: '',
        sortField: '' as TransactionSortField | ''
    });

    const { accessCodes, debouncedSearchTerm, loading, page, pageSize, searchTerm, serverTotalCount, sortDir, sortField } = state;

    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fetchSeqRef = useRef(0);

    const trimmedResourceKey = resourceKey?.trim() ?? '';
    const isFiltered = trimmedResourceKey.length > 0;
    const trimmedSearch = debouncedSearchTerm.trim();

    // Both lookups page, sort and search on the server. The resource-filtered variant used to
    // pull every access code the partner had ever had and then run one content query per
    // transaction just to render ten rows.
    const fetchCodes = async () => {

        // Sequence guard so a slow earlier request can never overwrite a newer one's rows.
        const seq = ++fetchSeqRef.current;

        try {

            setState({ loading: true });

            const result = isFiltered
                ? await TransactionApi.GetByResourcePaged(trimmedResourceKey, page, pageSize, sortField, sortDir, trimmedSearch)
                : await TransactionApi.GetAllPaged(page, pageSize, sortField, sortDir, trimmedSearch);

            if (seq === fetchSeqRef.current) {

                setState({ accessCodes: result?.Items ?? [], loading: false, serverTotalCount: result?.TotalCount ?? 0 });

            }

        } catch {

            if (seq === fetchSeqRef.current) {

                setState({ accessCodes: [], loading: false, serverTotalCount: 0 });

            }

        }

    };

    // One fetch effect: every server input funnels through here, so there is exactly one request
    // per change and no mount guard to get wrong.
    useEffect(() => {

        if (show) {

            fetchCodes();

        }

    }, [show, trimmedResourceKey, page, pageSize, sortField, sortDir, debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset paging, sort and search on close so a reopen starts clean.
    useEffect(() => {

        if (!show) {

            setState({ debouncedSearchTerm: '', page: 1, pageSize: PAGE_SIZE, searchTerm: '', sortDir: '', sortField: '' });

        }

    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce the search box. Note the parent's page is moved to 1 by setSearchTerm on the RAW
    // keystroke, not here: GMetrixTable zeroes its own internal skip the moment searchTerm changes
    // and deliberately does not call onServerPageChange, so waiting for the debounce would leave
    // the pager reading page 1 while page-N rows were still on screen.
    useEffect(() => {

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        if (searchTerm !== debouncedSearchTerm) {

            searchDebounceRef.current = setTimeout(() => setState({ debouncedSearchTerm: searchTerm, page: 1 }), SEARCH_DEBOUNCE_MS);

        }

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ page: Math.floor(skip / take) + 1, pageSize: take });

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const next = sort.length > 0 ? sort[0] : null;

        setState({
            page: 1,
            sortDir: next?.dir ?? '',
            sortField: (next?.field ?? '') as TransactionSortField | ''
        });

    };

    const handleSelect = (accessCode: string) => {

        onSelect(accessCode);
        onClose();

    };

    const columns: GMetrixTableColumnProps<Transaction>[] = [
        {
            field: 'Name',
            title: formatMessage({ id: 'access_code.b5fc1b57', defaultMessage: 'Access Code' })
        },
        {
            field: 'UsesRemaining',
            title: formatMessage({ id: 'uses_remaining.d26b9e27', defaultMessage: 'Uses Remaining' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.ExpirationDateString || '—'}
                </td>
            ),
            field: 'ExpirationDateString',

            // 'Expires' is not in the proc's sort-column allowlist, so it was silently rewritten
            // to 'Name' while the header still painted a sort arrow on this column.
            sortField: 'EndDate',
            title: formatMessage({ id: 'expires.84d49534', defaultMessage: 'Expires' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    <GMetrixButton onClick={() => handleSelect(dataItem.Codes)} variant="primary">
                        <FormattedMessage defaultMessage="Select" id="select.57e6e9c7" />
                    </GMetrixButton>
                </td>
            ),
            sortable: false,
            title: '',
            width: '1%'
        }
    ];

    return (
        <GMetrixDialog
            actionButtonClick={onClose}
            actionButtonDisabled={false}
            actionButtonText={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
            dialogLabel={formatMessage({ id: 'look_up_an_access_code.86f0782f', defaultMessage: 'Look up an access code' })}
            onCancel={onClose}
            show={show}
            showCancelButton={false}
            size="lg"
        >
            <p className="text-muted small">
                {isFiltered
                    ? formatMessage({ id: 'showing_only_access_codes_that_include_t.6284bef8', defaultMessage: 'Showing only access codes that include the selected resource. Select the one you want students to use when launching it.' })
                    : formatMessage({ id: 'select_the_access_code_you_want_students.4f925d23', defaultMessage: 'Select the access code you want students to use when launching this resource.' })}
            </p>
            <GMetrixTable
                serverSidePagination
                columnData={columns}
                defaultItemsPerPage={PAGE_SIZE}
                loading={loading}
                noDataMessage={isFiltered ? formatMessage({ id: 'no_access_codes_include_the_selected_res.1172e5d4', defaultMessage: 'No access codes include the selected resource.' }) : formatMessage({ id: 'no_access_codes_found.2ac9ae6b', defaultMessage: 'No access codes found.' })}
                onServerPageChange={handleServerPageChange}
                onServerSortChange={handleServerSortChange}
                resetPageSignal={`${show}|${trimmedResourceKey}|${debouncedSearchTerm}`}
                rowData={accessCodes}
                searchTerm={searchTerm}
                serverTotalCount={serverTotalCount}
                setSearchTerm={val => setState({ page: 1, searchTerm: val })}
            />
        </GMetrixDialog>
    );

};
